import axios, { AxiosRequestConfig, AxiosResponse, ResponseType } from 'axios';
import axiosRetry from 'axios-retry';
import { getAuth, User } from 'firebase/auth';
import { getApp } from 'firebase/app';

// Retry idempotent failures at the transport level. (Previously this was a
// dead `'axios-retry': { retries: 3 }` request *header* — axios stringified the
// object to "[object Object]" and sent it as a junk header, forcing a CORS
// preflight on every request for zero benefit.)
axiosRetry(axios, { retries: 3 });

export async function getRaw<T = string>(url: string, responseType: ResponseType = 'text'): Promise<T> {
  const response = await axios.get(url, { responseType: responseType, timeout: 10 * 1000 });
  return response.data;
}

const resolveToken = async (user: User) => {
  console.log(`Resolving token.`);
  const userToken = await user.getIdToken();
  console.log('User token resolved.', !!userToken);

  if (!userToken) {
    throw new Error(`No token found for user.`);
  }

  return `Bearer ${userToken}`;
};

export class HttpUtils {
  static readAsDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
    });
  }

  private static getApiUrl(baseUrl: string, ctxPath: string): string {
    return `${baseUrl}/${ctxPath}`.replace(/\/?$/, '/');
  }

  private static async auth(noAuth = false): Promise<string> {
    if (noAuth) {
      return '';
    }

    const auth = getAuth(getApp());
    if (!auth.currentUser) {
      console.log('User no yet authenticated, waiting for auth state event.');
      return new Promise((resolve, reject) => {
        const unsubCallback = auth.onAuthStateChanged(
          (user) => {
            if (user) {
              unsubCallback();
              resolveToken(user).then(resolve).catch(reject);
            }
          },
          (err) => console.error(`An error occurred while looking for auth state change events. [${err}]`),
        );
      });
    } else {
      return resolveToken(auth.currentUser);
    }
  }

  static async post<R = void>(baseUrl: string, ctxPath: string, body?: unknown, params?: unknown, noAuth = false): Promise<R> {
    const response = await axios.post<unknown, AxiosResponse<R>>(HttpUtils.getApiUrl(baseUrl, ctxPath), body, {
      responseType: 'json',
      params: params,
      headers: {
        Authorization: await HttpUtils.auth(noAuth),
      },
    } as unknown as AxiosRequestConfig);
    return response.data;
  }

  static async delete<R = void>(baseUrl: string, ctxPath: string, params?: unknown): Promise<R> {
    const response = await axios.delete<unknown, AxiosResponse<R>>(HttpUtils.getApiUrl(baseUrl, ctxPath), {
      responseType: 'json',
      params: params,
      headers: {
        Authorization: await HttpUtils.auth(),
      },
    } as unknown as AxiosRequestConfig);
    return response.data;
  }

  static async put<R>(baseUrl: string, ctxPath: string, body: unknown, params?: unknown): Promise<R> {
    const response = await axios.put<unknown, AxiosResponse<R>>(HttpUtils.getApiUrl(baseUrl, ctxPath), body, {
      responseType: 'json',
      params: params,
      headers: {
        Authorization: await HttpUtils.auth(),
      },
    } as unknown as AxiosRequestConfig);
    return response.data;
  }

  static async get<R>(baseUrl: string, ctxPath: string, params?: unknown, noAuth = false): Promise<R> {
    const apiUrl = HttpUtils.getApiUrl(baseUrl, ctxPath);
    const authorization = await HttpUtils.auth(noAuth);
    const response = await axios.get<unknown, AxiosResponse<R>>(apiUrl, {
      responseType: 'json',
      params: params,
      headers: {
        Authorization: authorization,
      },
    } as unknown as AxiosRequestConfig);
    return response.data;
  }

  static async getBlob(baseUrl: string, ctxPath: string): Promise<Blob> {
    const apiUrl = HttpUtils.getApiUrl(baseUrl, ctxPath);
    const authorization = await HttpUtils.auth();
    const response = await axios.get<Blob, AxiosResponse<Blob>>(apiUrl, {
      responseType: 'blob',
      headers: {
        Authorization: authorization,
      },
    } as unknown as AxiosRequestConfig);
    return response.data;
  }
}
